/**
 * V2 source path: customer/midas/components/v2/MidasFleetHistoryPickerV2.tsx
 * V1 source: scratch — V1 had stacked Fleet + Recent History sections, no picker
 * Strategy: scratch-with-reused-handlers
 * Verified no-MUI: yes
 * Phase: 3 — Round 17 (Alt A Calm — underline tab strip)
 *
 * Round 17 — Alt A Calm:
 *   Replaced the iOS-style segmented pill with a web-native underline tab
 *   strip per Paper artboard 59R-0. Active tab = Sora 13/600 graphite #2A2824
 *   with a 2px graphite underline; inactive tab = Sora 13/500 muted-stone
 *   #8C8375. Mono count next to each label keeps the data accent.
 *
 * Tap targets: each tab is min-h-11 (44px) per TOUCH_UX_GUIDELINES.md.
 *
 * Animation: only the underline color/opacity transitions. Respects
 * `prefers-reduced-motion` via `motion-reduce:transition-none`.
 */
import React from "react";

export type FleetHistoryTab = "fleet" | "history";

interface MidasFleetHistoryPickerV2Props {
  active: FleetHistoryTab;
  fleetCount: number;
  historyCount: number;
  onChange: (tab: FleetHistoryTab) => void;
}

interface TabButtonProps {
  label: string;
  count: number;
  isActive: boolean;
  ariaControls: string;
  onClick: () => void;
}

const TabButton: React.FC<TabButtonProps> = ({
  label,
  count,
  isActive,
  ariaControls,
  onClick,
}) => (
  <button
    type="button"
    role="tab"
    aria-selected={isActive}
    aria-controls={ariaControls}
    onClick={onClick}
    className="relative flex min-h-11 items-baseline gap-1.5 px-1 py-2.5 outline-none transition-colors duration-150 focus-visible:ring-2 focus-visible:ring-midas-terracotta focus-visible:ring-offset-2 focus-visible:ring-offset-midas-bone motion-reduce:transition-none"
  >
    <span
      className={`text-[13px] tracking-[-0.01em] ${
        isActive
          ? "font-semibold text-midas-graphite"
          : "font-medium text-midas-muted-stone"
      }`}
      style={{ fontFamily: "'Sora', sans-serif" }}
    >
      {label}
    </span>
    <span
      className={`text-[10px] font-medium ${
        isActive ? "text-midas-graphite" : "text-midas-muted-stone"
      }`}
      style={{ fontFamily: "'JetBrains Mono', monospace" }}
    >
      {count}
    </span>
    {/* Underline — solid graphite when active, transparent otherwise. -1px so it sits flush with the strip's bottom hairline. */}
    <span
      aria-hidden="true"
      className="pointer-events-none absolute -bottom-px left-0 right-0 h-0.5 transition-[background-color,opacity] duration-150 motion-reduce:transition-none"
      style={{
        backgroundColor: isActive ? "#2A2824" : "transparent",
        opacity: isActive ? 1 : 0,
      }}
    />
  </button>
);

const MidasFleetHistoryPickerV2: React.FC<MidasFleetHistoryPickerV2Props> = ({
  active,
  fleetCount,
  historyCount,
  onChange,
}) => {
  const isFleet = active === "fleet";

  return (
    <div
      role="tablist"
      aria-label="Sidebar mode"
      className="flex select-none items-end gap-6 border-b border-[rgba(44,38,32,0.08)] px-1"
    >
      <TabButton
        label="Fleet"
        count={fleetCount}
        isActive={isFleet}
        ariaControls="midas-sidebar-fleet-panel"
        onClick={() => onChange("fleet")}
      />
      <TabButton
        label="History"
        count={historyCount}
        isActive={!isFleet}
        ariaControls="midas-sidebar-history-panel"
        onClick={() => onChange("history")}
      />
    </div>
  );
};

export default MidasFleetHistoryPickerV2;
